import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './AuthPage.css';
import { startNaverLogin } from './naverLogin';
import DuplicateProfileNotice, { toDuplicateProfile } from './DuplicateProfileNotice';
import TermsAgreement from './TermsAgreement';
import NoticeModal from './NoticeModal';
import { isSignupAge, latestSignupBirthDate, underAgeMessage } from './ageLimit';
import { fetchTerms, requiredAgreed, toAgreements } from './terms';
import { clearSavedEmail, readSavedEmail, saveEmail } from './savedEmail';

const SERVER = (process.env.REACT_APP_SERVER || '').replace(/\/$/, '');

const genderOptions = [['male', '남'], ['female', '여']];

function CaptchaCanvas({ code }) {
    const canvasRef = useRef(null);
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const W = canvas.width, H = canvas.height;
        // 배경
        ctx.fillStyle = '#e8eef2';
        ctx.fillRect(0, 0, W, H);
        // 배경 노이즈 점
        for (let i = 0; i < 120; i++) {
            ctx.fillStyle = `rgba(${80 + Math.random()*80|0},${100+Math.random()*80|0},${120+Math.random()*80|0},${0.3+Math.random()*0.4})`;
            ctx.beginPath();
            ctx.arc(Math.random()*W, Math.random()*H, Math.random()*2.5, 0, Math.PI*2);
            ctx.fill();
        }
        // 배경 노이즈 선
        for (let i = 0; i < 5; i++) {
            ctx.strokeStyle = `rgba(${60+Math.random()*100|0},${80+Math.random()*80|0},${100+Math.random()*80|0},0.35)`;
            ctx.lineWidth = 1 + Math.random();
            ctx.beginPath();
            ctx.moveTo(Math.random()*W, Math.random()*H);
            ctx.bezierCurveTo(Math.random()*W, Math.random()*H, Math.random()*W, Math.random()*H, Math.random()*W, Math.random()*H);
            ctx.stroke();
        }
        // 글자
        const charW = W / code.length;
        [...code].forEach((ch, i) => {
            ctx.save();
            const cx = charW * i + charW / 2;
            const cy = H / 2 + (Math.random() - 0.5) * 10;
            ctx.translate(cx, cy);
            ctx.rotate((Math.random() - 0.5) * 0.55);
            const size = 22 + Math.random() * 8;
            ctx.font = `${Math.random() > 0.5 ? 'bold' : '900'} ${size}px monospace`;
            ctx.fillStyle = `rgb(${20+Math.random()*60|0},${40+Math.random()*60|0},${60+Math.random()*80|0})`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            // 약간의 스케일 왜곡
            ctx.scale(1, 0.85 + Math.random() * 0.3);
            ctx.fillText(ch, 0, 0);
            ctx.restore();
        });
    }, [code]);
    return <canvas ref={canvasRef} width={180} height={56} className="auth-captcha-canvas" aria-hidden="true" />;
}

CaptchaCanvas.propTypes = { code: PropTypes.string.isRequired };

export function calculateAge(birthDate, today = new Date()) {
    if (!birthDate) return '';
    const [year, month, day] = birthDate.split('-').map(Number);
    if (!year || !month || !day) return '';
    let age = today.getFullYear() - year;
    if (today.getMonth() + 1 < month || (today.getMonth() + 1 === month && today.getDate() < day)) age -= 1;
    return age >= 0 ? age : '';
}

export default function AuthPage({ mode }) {
    const register = mode === 'register';
    const login = mode === 'login';
    const navigate = useNavigate();
    const location = useLocation();
    const [values, setValues] = useState({ name: '', birthDate: '', gender: '', affiliation: '', email: '', password: '', passwordConfirm: '' });
    const [emailPending, setEmailPending] = useState(false);
    const emailRequestPending = useRef(false);
    const registrationPending = useRef(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [loginFailCount, setLoginFailCount] = useState(0);
    const [captchaCode, setCaptchaCode] = useState('');
    const [captchaInput, setCaptchaInput] = useState('');
    const [code, setCode] = useState('');
    const [emailConfirmed, setEmailConfirmed] = useState(false);
    const [registerToken, setRegisterToken] = useState('');
    const [codeSent, setCodeSent] = useState(false);
    const [codeExpiresAt, setCodeExpiresAt] = useState(null);
    const [remainingSeconds, setRemainingSeconds] = useState(0);
    const [codeError, setCodeError] = useState('');
    const [registerDone, setRegisterDone] = useState(false);
    const [duplicateProfile, setDuplicateProfile] = useState(null);
    const [terms, setTerms] = useState([]);
    const [termsChecked, setTermsChecked] = useState({});
    const [rememberEmail, setRememberEmail] = useState(() => Boolean(readSavedEmail()));

    useEffect(() => {
        if (!register || !codeExpiresAt || emailConfirmed) return undefined;
        let alertShown = false;
        function updateTimer() {
            const remaining = Math.max(0, Math.ceil((codeExpiresAt - Date.now()) / 1000));
            setRemainingSeconds(remaining);
            if (!remaining && !alertShown) {
                alertShown = true;
                window.alert('인증번호 입력 시간이 지났습니다. 인증번호를 재전송해주세요.');
            }
        }
        updateTimer();
        const timer = setInterval(updateTimer, 1000);
        return () => clearInterval(timer);
    }, [register, codeExpiresAt, emailConfirmed]);

    useEffect(() => {
        setSubmitError('');
        const savedEmail = login ? readSavedEmail() : '';
        setValues({ name: '', birthDate: '', gender: '', affiliation: '', email: savedEmail, password: '', passwordConfirm: '' });
        setRememberEmail(Boolean(savedEmail));
        setTouched({});
        setLoginFailCount(0);
        setCaptchaCode('');
        setCaptchaInput('');
        setRegisterDone(false);
        setDuplicateProfile(null);
        setTermsChecked({});
    }, [mode, login]);

    useEffect(() => {
        if (!register) return undefined;
        let active = true;
        fetchTerms().then(termList => { if (active) setTerms(termList); });
        return () => { active = false; };
    }, [register]);

    function loginWithNaver() {
        try {
            startNaverLogin();
        } catch (error) {
            setSubmitError(error.message || '네이버 로그인을 시작하지 못했습니다. 다시 시도해주세요.');
        }
    }
    // 이메일 저장 체크를 바꾼다. 해제하면 저장된 이메일을 바로 지운다.
    function toggleRememberEmail(event) {
        setRememberEmail(event.target.checked);
        if (!event.target.checked) clearSavedEmail();
    }
    function generateCaptcha() {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%&';
        return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    }

    const countdown = `${String(Math.floor(remainingSeconds / 60)).padStart(2, '0')}:${String(remainingSeconds % 60).padStart(2, '0')}`;
    const [passwordTouched, setPasswordTouched] = useState(false);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const passwordValid = Array.from(values.password).length >= 8
        && !/\s/.test(values.password)
        && [/[a-z]/i, /[0-9]/, /[!-/:-@[-`{-~]/].filter(pattern => pattern.test(values.password)).length >= 2;
    const passwordError = register && (passwordTouched || values.password.length > 0) && !passwordValid;
    const age = useMemo(() => calculateAge(values.birthDate), [values.birthDate]);
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const [touched, setTouched] = useState({});
    const birth = new Date(`${values.birthDate}T00:00:00Z`);
    const birthValid = /^\d{4}-\d{2}-\d{2}$/.test(values.birthDate)
        && !Number.isNaN(birth.getTime()) && birth.toISOString().slice(0, 10) === values.birthDate
        && values.birthDate >= '1900-01-01' && values.birthDate <= today;
    const errors = {
        name: register && (values.name.trim().length < 2 || values.name.trim().length > 50) ? '이름을 2~50자로 입력해주세요.' : '',
        birthDate: register && !birthValid ? '올바른 생년월일을 입력해주세요.' : register && !isSignupAge(values.birthDate) ? underAgeMessage : '',
        gender: register && !genderOptions.some(([value]) => value === values.gender) ? '성별을 선택해주세요.' : '',
        email: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim()) || values.email.trim().length > 254 ? '올바른 이메일을 입력해주세요.' : '',
        passwordConfirm: register && values.password !== values.passwordConfirm ? '비밀번호가 일치하지 않아요' : '',
        password: !values.password || (register && !passwordValid) ? '비밀번호 입력 기준을 확인해주세요.' : '',
    };
    const termsValid = !register || (terms.length > 0 && requiredAgreed(terms, termsChecked));
    const formValid = !Object.values(errors).some(Boolean) && termsValid;
    const requiredMark = register && <span className="auth-required" aria-hidden="true">*</span>;
    function fieldProps(name) {
        return {
            onBlur: () => setTouched(previous => ({ ...previous, [name]: true })),
            'aria-invalid': Boolean(touched[name] && errors[name]),
            'aria-describedby': touched[name] && errors[name] ? `${name}-error` : undefined,
        };
    }
    function fieldError(name) {
        return touched[name] && errors[name] ? <small id={`${name}-error`} className="auth-field-error" role="alert">{errors[name]}</small> : null;
    }

    function change(event) {
        if (registrationPending.current) return;
        setSubmitError('');
        setDuplicateProfile(null);
        const { name, value } = event.target;
        if (name === 'passwordConfirm') setTouched(previous => ({ ...previous, passwordConfirm: true }));
        setValues((previous) => ({ ...previous, [name]: value }));
        if (name === 'email') { setEmailConfirmed(false); setRegisterToken(''); setCode(''); setCodeSent(false); setCodeExpiresAt(null); setRemainingSeconds(0); setCodeError(''); }
    }

    async function verifyEmail() {
        setTouched(previous => ({ ...previous, email: true }));
        if (errors.email || emailRequestPending.current || registrationPending.current) return;
        emailRequestPending.current = true;
        setEmailPending(true);
        try {
            const { data } = await axios.post(`${SERVER}/api/auth/email/send`, { email: values.email.trim(), purpose: 'register' }, { timeout: 45000 });
            if (data?.success === false) {
                window.alert(data.message || '이메일 발송에 실패했습니다. 다시 시도해주세요.');
                return;
            }
            setCodeSent(true); setCode(''); setEmailConfirmed(false);
            setCodeExpiresAt(Date.now() + 10 * 60 * 1000);
            setRemainingSeconds(600);
            setCodeError('');
            window.alert('인증번호를 발송했습니다. 이메일을 확인해주세요.');
        } catch (error) {
            window.alert(error.response?.data?.message || error.response?.data?.error || '이메일 발송에 실패했습니다. 잠시 후 다시 시도해주세요.');
        } finally {
            emailRequestPending.current = false;
            setEmailPending(false);
        }
    }

    async function confirmEmail() {
        if (emailRequestPending.current || registrationPending.current) return;
        if (!codeExpiresAt || Date.now() >= codeExpiresAt) {
            setRemainingSeconds(0);
            window.alert('인증번호 입력 시간이 지났습니다. 인증번호를 재전송해주세요.');
            return;
        }
        if (!/^\d{6}$/.test(code)) return;
        emailRequestPending.current = true;
        setEmailPending(true);
        try {
            const { data } = await axios.post(`${SERVER}/api/auth/email/verify`, { email: values.email.trim(), code, purpose: 'register' }, { timeout: 45000 });
            setEmailConfirmed(true);
            setRegisterToken(data?.registerToken || '');
            setCodeExpiresAt(null);
            setRemainingSeconds(0);
            setCodeError('');
        } catch (error) {
            const msg = error.response?.data?.message || error.response?.data?.error || '인증번호가 올바르지 않습니다.';
            if (error.response?.status === 429) {
                window.alert(msg);
            } else {
                setCodeError(msg);
            }
        } finally {
            emailRequestPending.current = false;
            setEmailPending(false);
        }
    }

    async function submit(event) {
        event.preventDefault();
        setTouched({ name: true, birthDate: true, gender: true, email: true, password: true, passwordConfirm: true });
        setPasswordTouched(true);
        const captchaRequired = login && loginFailCount >= 5;
        if (!formValid || emailRequestPending.current || registrationPending.current || (register && !emailConfirmed)) return;
        if (captchaRequired && captchaInput !== captchaCode) {
            setSubmitError('보안 문자가 올바르지 않습니다. 다시 입력해주세요.');
            setCaptchaCode(generateCaptcha());
            setCaptchaInput('');
            return;
        }
        registrationPending.current = true;
        setSubmitting(true); setSubmitError(''); setDuplicateProfile(null);
        try {
            if (register) {
                const body = {
                    name: values.name.trim(), age: calculateAge(values.birthDate), birthDate: values.birthDate,
                    gender: values.gender, email: values.email.trim(), password: values.password,
                    registerToken,
                    agreements: toAgreements(terms, termsChecked),
                    ...(values.affiliation.trim() ? { affiliation: values.affiliation.trim() } : {}),
                };
                const { status, data } = await axios.post(`${SERVER}/api/auth/register`, body, { withCredentials: true, timeout: 45000 });
                if (status !== 201 || !data?.accessToken || !data?.user) {
                    setSubmitError(data?.message || '회원가입 응답을 확인하지 못했습니다. 다시 시도해주세요.');
                    return;
                }
            } else {
                const { data } = await axios.post(`${SERVER}/api/auth/login`, { email: values.email.trim(), password: values.password }, { withCredentials: true, timeout: 45000 });
                if (!data?.accessToken || !data?.user) {
                    setSubmitError(data?.message || '로그인 응답을 확인하지 못했습니다. 다시 시도해주세요.');
                    return;
                }
                try {
                    sessionStorage.setItem('garam-user-auth', JSON.stringify({ accessToken: data.accessToken, user: data.user }));
                } catch {
                    setSubmitError('로그인은 완료되었지만 로그인 정보를 저장하지 못했습니다. 브라우저 저장 설정을 확인해주세요.');
                    return;
                }
                if (rememberEmail) saveEmail(values.email.trim());
                else clearSavedEmail();
                setLoginFailCount(0);
            }
            window.dispatchEvent(new Event('garam-auth-change'));
            if (register) {
                setRegisterDone(true);
            } else {
                // 로그인 필요 페이지에서 넘어왔으면 그 페이지로 돌려보낸다.
                const from = location.state?.from;
                navigate(from ? `${from.pathname}${from.search || ''}` : "/ai-clients", { replace: Boolean(from) });
            }
        } catch (error) {
            const duplicate = register && toDuplicateProfile(error.response?.data);
            if (duplicate) {
                setDuplicateProfile(duplicate);
                return;
            }
            if (login) {
                const newCount = loginFailCount + 1;
                setLoginFailCount(newCount);
                if (newCount >= 5) { setCaptchaCode(generateCaptcha()); setCaptchaInput(''); }
            }
            setSubmitError(error.response?.data?.message || error.response?.data?.error || (register ? '회원가입 요청에 실패했습니다. 잠시 후 다시 시도해주세요.' : '로그인 요청에 실패했습니다. 잠시 후 다시 시도해주세요.'));
        } finally {
            registrationPending.current = false;
            setSubmitting(false);
        }
    }

    return <div className="auth-page"><main className="auth-card">
        <span className="auth-kicker">GARAM MIND</span>
        <h1>{register ? '회원가입' : '로그인'}</h1>
        <p>{register ? '가람마인드 서비스를 이용하기 위한 기본 정보를 입력해주세요.' : '가입한 이메일로 가람마인드의 서비스를 이용해보세요.'}</p>
        <form onSubmit={submit} noValidate>
            {register && <>
                <small><span className="auth-required">*</span> 필수 입력 항목</small>
                <label><span>이메일 {requiredMark}</span><div className="auth-email"><input required type="email" name="email" disabled={emailPending || submitting} {...fieldProps('email')} value={values.email} onChange={change} maxLength={254} autoComplete="email" />{register && <button type="button" disabled={emailPending || submitting || emailConfirmed} onClick={verifyEmail}>{emailPending ? '처리 중…' : emailConfirmed ? '인증완료' : codeSent ? '인증번호 재발송' : '인증번호 발송'}</button>}</div>{fieldError('email')}</label>
                {codeSent && !emailConfirmed && <label>인증번호<div className="auth-email"><input aria-label="인증번호" aria-describedby="verification-timer" disabled={emailPending || remainingSeconds === 0} value={code} onChange={event => { setCode(event.target.value.replace(/\D/g, '').slice(0, 6)); setCodeError(''); }} inputMode="numeric" autoComplete="one-time-code" maxLength={6} /><button type="button" disabled={emailPending || code.length !== 6 || remainingSeconds === 0} onClick={confirmEmail}>인증하기</button></div><small id="verification-timer" className={remainingSeconds === 0 ? 'auth-field-error' : undefined}>유효시간 {countdown}</small>{codeError && <small className="auth-field-error" role="alert">{codeError}</small>}</label>}
                <div className="auth-password-field"><label htmlFor="auth-password">비밀번호 {requiredMark}</label><div className={!register ? 'auth-password-control' : undefined}><input id="auth-password" required type={!register && passwordVisible ? 'text' : 'password'} name="password" value={values.password} onChange={change} onBlur={() => setPasswordTouched(true)} aria-invalid={passwordError} aria-describedby={register ? 'password-help' : undefined} minLength={8} maxLength={72} autoComplete={register ? 'new-password' : 'current-password'} />
                    {!register && <button className="auth-password-toggle" type="button" aria-label={passwordVisible ? '비밀번호 숨기기' : '비밀번호 보기'} aria-controls="auth-password" onClick={() => setPasswordVisible(visible => !visible)}>{passwordVisible ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}</button>}
                    </div></div>
                    {register && <><small id="password-help" className={passwordError ? 'auth-field-error' : undefined} aria-live="polite">{passwordError && '비밀번호 기준을 충족하지 못했습니다. '}공백 없이 8자 이상, 영문·숫자·특수문자 중 2종류 이상을 포함해주세요.</small><label><span>비밀번호 확인 {requiredMark}</span><input type="password" required name="passwordConfirm" {...fieldProps('passwordConfirm')} value={values.passwordConfirm} onChange={change} minLength={8} maxLength={72} autoComplete="new-password" />{fieldError('passwordConfirm')}</label></>}
                    <label><span>이름 {requiredMark}</span><input required name="name" {...fieldProps('name')} value={values.name} onChange={change} minLength={2} maxLength={50} autoComplete="name" />{fieldError('name')}</label>
                    <div className="auth-row">
                        <label><span>생년월일 {requiredMark}</span><input required type="date" name="birthDate" {...fieldProps('birthDate')} value={values.birthDate} onChange={change} min="1900-01-01" max={latestSignupBirthDate()} autoComplete="bday" />{fieldError('birthDate')}</label>
                        <label>나이<input value={age === '' ? '' : `${age}세`} readOnly aria-describedby="age-help" /></label>
                    </div>
                    <small id="age-help">나이는 생년월일을 기준으로 자동 계산됩니다.</small>
                    <label><span>성별 {requiredMark}</span><select required name="gender" {...fieldProps('gender')} value={values.gender} onChange={change}><option value="">선택해주세요</option>{genderOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>{fieldError('gender')}</label>
                    <label>소속 (선택)<input name="affiliation" value={values.affiliation} onChange={change} maxLength={150} placeholder="학생, 직장인, 연구원 등" autoComplete="organization" /></label>
                    <TermsAgreement terms={terms} checked={termsChecked} onChange={setTermsChecked} disabled={submitting} />

            </>}
            {login && <>
                <label>
                    이메일
                    <div>
                        <input required maxLength={254} type="email" name="email" value={values.email} onChange={change} autoComplete="email" />
                    </div>
                </label>
                <label>
                    비밀번호
                    <div className="auth-password-control">
                        <input id="auth-password" required minLength={8} maxLength={72} type={passwordVisible ? 'text' : 'password'} name="password" value={values.password} onChange={change} autoComplete="current-password" />
                        <button className="auth-password-toggle" type="button" aria-label={passwordVisible ? '비밀번호 숨기기' : '비밀번호 보기'} aria-controls="auth-password" onClick={() => setPasswordVisible(visible => !visible)}>{passwordVisible ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}</button>
                    </div>
                </label>
                <label className="auth-remember"><input type="checkbox" checked={rememberEmail} onChange={toggleRememberEmail} />이메일 저장</label>
            </>}
            {login && loginFailCount >= 5 && <label>
                보안 문자 <span aria-live="polite" className="auth-captcha-hint">(그림 속 문자를 입력해주세요)</span>
                <div className="auth-captcha">
                    <CaptchaCanvas code={captchaCode} />
                    <button type="button" className="auth-captcha-refresh" onClick={() => { setCaptchaCode(generateCaptcha()); setCaptchaInput(''); }} aria-label="보안 문자 새로고침" title="새로고침">↻</button>
                </div>
                <input value={captchaInput} onChange={event => { setCaptchaInput(event.target.value); setSubmitError(''); }} maxLength={6} autoComplete="off" placeholder="보안 문자 6자리 입력" />
            </label>}
            {submitError && <div className="auth-error" role="alert">{submitError}</div>}
            {duplicateProfile && <DuplicateProfileNotice profile={duplicateProfile} onNaverLogin={loginWithNaver} />}
            <button className="auth-submit" type="submit" disabled={submitting || !formValid || emailPending || (register && !emailConfirmed)}>{submitting ? '가입 중…' : register ? '가입하고 시작하기' : '로그인'}</button>
        </form>
        {login && <div className="auth-social">
            <hr className="auth-social-divider" />
            <p className="auth-social-title">SNS 계정으로 간편 로그인</p>
            <div className="auth-social-buttons" role="group" aria-label="소셜 로그인">
                <button type="button" className="auth-social-button auth-social-naver" aria-label="네이버로 로그인" onClick={loginWithNaver}>
                    {/* 임시 심볼: 추후 제공받은 공식 이미지로 교체 */}
                    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <path fill="currentColor" d="M3 3h6l6 9V3h6v18h-6l-6-9v9H3z" />
                    </svg>
                </button>
                {/* 카카오 로그인 연동 시 다시 표시
                <button type="button" className="auth-social-button auth-social-kakao" aria-label="카카오로 로그인" onClick={() => window.alert('카카오 로그인은 준비 중입니다.')}>
                    <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden="true" focusable="false">
                        <path fill="currentColor" d="M14 4C7.37 4 2 8.1 2 13.15c0 3.2 2.15 6.01 5.39 7.65l-1.1 4.02c-.1.36.31.64.62.43l4.77-3.16c.75.12 1.53.18 2.32.18 6.63 0 12-4.08 12-9.12S20.63 4 14 4z" />
                    </svg>
                </button>
                */}
            </div>
        </div>}
        {registerDone && <NoticeModal title="회원가입이 완료되었습니다!" message={`${values.name.trim()}님, 가람마인드에 오신 것을 환영합니다.`} onConfirm={() => navigate('/', { replace: true })} />}
        <p className="auth-switch">{register ? '이미 계정이 있나요?' : '아직 계정이 없나요?'} <Link to={register ? '/login' : '/register'}>{register ? '로그인' : '회원가입'}</Link>{!register && <span className="auth-recovery-links"> [<Link to="/find-email">이메일 찾기</Link> / <Link to="/find-password">비밀번호 찾기</Link>]</span>}</p>
    </main></div>;
}

AuthPage.propTypes = { mode: PropTypes.oneOf(['login', 'register']).isRequired };
